<template>
  <div class='theme-multiple-files__holder'>
    <h3 class="mb-2" v-if="hint && !managedSubmit">{{ hint }}</h3>
    <label :for='id' class='theme-multiple-files' v-if="!managedSubmit">
      <span class='theme-btn theme-btn--small theme-multiple-files__desktop-btn w-full mb-2' :class="{'theme-btn--white': white, 'theme-btn--theme': !white}">
        <i class='icon-upload' style='font-size: 20px;'/>
        <span class='theme-multiple-files__button-text'>
          {{ locales && locales.common && locales.common.choose_file }}
        </span>
      </span>
    </label>

    <!-- MANAGED MODE: gallery tiles, drag reorder, remove, add-tile, counters. Invalid files
         are rejected via async decode validation and surfaced through one batched SweetAlert;
         no rejected-list UI is rendered in managed mode. -->
    <template v-if="managedSubmit">
      <p v-if="managedHint" class="theme-multiple-files__managed-hint mb-3">{{ managedHint }}</p>
      <div class="theme-gallery"
           @dragover.prevent
           @drop.prevent="onDrop">
        <draggable
          tag="ul"
          class="theme-gallery__list"
          v-model="draggableAccepted"
          :draggable="'.theme-gallery__item--draggable'"
          v-bind="dragOpts">
          <li v-for="(rec, index) in draggableAccepted"
              :key="rec.recordId"
              class="theme-gallery__item theme-gallery__item--draggable">
            <div class="theme-gallery__item-inner" style="position: relative;">
              <img v-if="rec.isImage && rec.previewUrl"
                   :src="rec.previewUrl"
                   :alt="rec.file ? rec.file.name : ''"
                   class="theme-gallery__thumbnail"
                   style="object-fit: cover;"
                   @error="handleImageError(rec)" />
              <div v-else
                   class="theme-gallery__thumbnail"
                   style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
                <i class="icon-file" style="font-size: 28px; display:block;"></i>
                <span class="text-xs">{{ rec.file ? fileExtension(rec.file.name) : '' }}</span>
              </div>
              <button type="button"
                      class="theme-gallery__close"
                      :aria-label="removeLabel"
                      :title="removeLabel"
                      style="z-index: 2; font-weight: bold; line-height: 1;"
                      @click.prevent.stop="requestRemoveRecord(rec.recordId)">
                <span aria-hidden="true">&times;</span>
              </button>
              <span v-if="statusBadge(rec.recordId)"
                    class="theme-gallery__status"
                    :class="'theme-gallery__status--' + statusBadge(rec.recordId)"
                    :title="errorMessage(rec.recordId) || statusLabel(rec.recordId)"
                    style="position: absolute; bottom: 4px; left: 4px; padding: 2px 6px; font-size: 11px; line-height: 1; border-radius: 3px; z-index: 2;">
                {{ statusLabel(rec.recordId) }}
              </span>
              <div v-if="showRetry && statusBadge(rec.recordId) === 'failed'"
                   class="theme-gallery__failed-overlay"
                   style="position: absolute; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 3;">
                <button type="button"
                        class="theme-btn theme-btn--small theme-btn--white"
                        :title="retryLabel"
                        @click.prevent.stop="onRetryClick(rec.recordId)">
                  {{ retryLabel }}
                </button>
              </div>
            </div>
            <div class="theme-gallery__item-index">{{ index + 1 }}</div>
            <p v-if="statusBadge(rec.recordId) === 'failed' && errorMessage(rec.recordId)"
               class="text-xs text-red-700 mt-1"
               style="max-width: 120px; word-break: break-word;">
              {{ errorMessage(rec.recordId) }}
            </p>
          </li>

          <li slot="footer"
              class="theme-gallery__item-to-drop"
              :title="addMoreLabel"
              :aria-label="addMoreLabel"
              @click="triggerNativeInput">
            <i class="icon-plus" style="font-size: 20px; margin-bottom: 6px;"/>
            <span>{{ addMoreLabel }}</span>
          </li>
        </draggable>
      </div>

      <div v-if="acceptedRecords.length > 0" class="theme-multiple-files__counters mt-3 text-sm">
        <strong>{{ acceptedRecords.length }}</strong>
        <span class="ml-1">{{ countLabel }}</span>
        <span class="mx-1">&middot;</span>
        <span>{{ formattedTotalSize }}</span>
      </div>
    </template>

    <!-- LEGACY MODE: original filename list (preserved for native-FileList consumers) -->
    <template v-else>
      <div>
        <div v-if="Object.keys(legacyFiles).length > 0" class="flex gap-2 flex-wrap">
          <span v-for="file in legacyFiles" class='theme-multiple-files-text'>
            {{ file.name }}
          </span>
        </div>
        <span v-else class='theme-multiple-files-text'>{{ locales && locales.common && locales.common.no_file_chosen }}</span>
      </div>
    </template>

    <input
      style='display: none;'
      :class='inputClass'
      type='file'
      :accept='accept'
      :name="name"
      :id='id'
      @change='change'
      :ref="id"
      multiple
    />
  </div>
</template>

<script>
  /*
   * ThemeMultipleFiles
   *
   * Two operating modes:
   *
   *   1) Legacy mode (managedSubmit=false, DEFAULT):
   *      - The native <input type="file"> retains its FileList after selection.
   *      - Parent surfaces that serialize the form via jQuery or FormData reading
   *        input[type=file].files continue to work unchanged.
   *      - UI shows only filenames (original behavior).
   *
   *   2) Managed mode (managedSubmit=true):
   *      - Files are owned by this component's reactive `records` array.
   *      - The native input is cleared after every selection so jQuery-based
   *        serializers do NOT collect the files (avoiding double-append).
   *      - UI renders an admin-gallery-style picker: 120x120 tiles, drag-to-reorder
   *        (vuedraggable, mobile-safe 400ms delay), an add-more tile, per-tile
   *        remove button, 1-based index, counter and rejected-file warnings.
   *      - Drag reorder mutates `records` via the `draggableAccepted` setter so
   *        rejected records are preserved at the tail; getSelectedFiles() returns
   *        ready records in UI order.
   *      - Parent MUST read files at submit time via:
   *           this.$refs.<ref>.getSelectedFiles()
   *        and append them to its own FormData under the `name` prop.
   *
   * Future XHR compatibility:
   *   getSelectedFileRecords() exposes per-file metadata (recordId, file, previewUrl,
   *   isImage, status). A future per-file XHR uploader can reuse these records and
   *   add progress/error state without breaking this component's contract.
   */
  import {mapGetters} from 'vuex';
  import draggable from 'vuedraggable';

  var SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];
  var SUPPORTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
  var SUPPORTED_FORMATS_LABEL = 'jpg, jpeg, png, gif';

  export default {
    components: {
      draggable,
    },
    props: {
      name: {
        default: 'file',
        type: String,
      },
      accept: {
        default: '',
        type: String,
      },
      id: {
        default: '',
        type: String,
      },
      inputClass: {
        default: '',
        type: String,
      },
      white: {
        default: false,
        type: Boolean,
      },
      hint: {
        default: '',
        type: String,
      },
      managedSubmit: {
        default: false,
        type: Boolean,
      },
      managedHint: {
        default: '',
        type: String,
      },
      addLabel: {
        default: '',
        type: String,
      },
      statusMap: {
        default: function () { return {}; },
        type: Object,
      },
      showRetry: {
        default: false,
        type: Boolean,
      },
      retryLabel: {
        default: 'Opnieuw proberen',
        type: String,
      },
      confirmBeforeRemove: {
        default: false,
        type: Boolean,
      },
      removeConfirmText: {
        default: '',
        type: String,
      },
      removeConfirmCancel: {
        default: '',
        type: String,
      },
      removeConfirmOk: {
        default: '',
        type: String,
      },
    },
    data() {
      return {
        records: [],
        legacyFiles: {},
        nextRecordId: 1,
        pendingRemoveIds: {},
        dragOpts: {
          delay: 400,
          delayOnTouchOnly: true,
          touchStartThreshold: 3,
          fallbackTolerance: 3,
        },
      };
    },
    computed: {
      ...mapGetters(['locales']),
      acceptedRecords() {
        return this.records.filter(function (r) { return r.status === 'ready'; });
      },
      rejectedRecords() {
        return this.records.filter(function (r) { return r.status === 'rejected'; });
      },
      draggableAccepted: {
        get() {
          return this.acceptedRecords;
        },
        set(nextAccepted) {
          var rejected = this.records.filter(function (r) { return r.status === 'rejected'; });
          this.records = nextAccepted.concat(rejected);
          var self = this;
          this.$nextTick(function () {
            self.$emit('records-changed', self.getSelectedFileRecords());
          });
        },
      },
      acceptImageOnly() {
        if (!this.accept) return false;
        var parts = this.accept.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (parts.length === 0) return false;
        return parts.every(function (p) {
          return p === 'image/*'
            || p.indexOf('image/') === 0
            || /^\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/i.test(p);
        });
      },
      totalBytes() {
        return this.acceptedRecords.reduce(function (sum, r) {
          return sum + (r.file && r.file.size ? r.file.size : 0);
        }, 0);
      },
      formattedTotalSize() {
        return this.formatBytes(this.totalBytes);
      },
      countLabel() {
        var c = this.acceptedRecords.length;
        if (this.locales && this.locales.common) {
          if (c === 1 && this.locales.common.file) return this.locales.common.file;
          if (c !== 1 && this.locales.common.files) return this.locales.common.files;
        }
        return c === 1 ? 'file' : 'files';
      },
      removeLabel() {
        if (this.locales && this.locales.common && this.locales.common.remove) {
          return this.locales.common.remove;
        }
        return 'Remove';
      },
      addMoreLabel() {
        if (this.addLabel) {
          return this.addLabel;
        }
        if (this.locales && this.locales.common && this.locales.common.upload) {
          return this.locales.common.upload;
        }
        return 'Add';
      },
      rejectedNonImageMessage() {
        if (this.locales && this.locales.common && this.locales.common.invalid_image_file) {
          return this.locales.common.invalid_image_file;
        }
        return 'Not an image file';
      },
    },
    watch: {
      // When the parent reports a successful upload AND the server emitted a smaller
      // thumbnail URL, swap the tile preview from the local Blob URL to that server URL
      // and revoke the Blob. Keeps memory in check on large batches without changing
      // any consumer that does not surface serverSmallUrl.
      statusMap: {
        deep: true,
        handler: function (newMap) {
          if (!newMap) return;
          for (var i = 0; i < this.records.length; i++) {
            var rec = this.records[i];
            if (!rec) continue;
            var entry = newMap[rec.recordId];
            if (!entry) continue;
            if (entry.status !== 'uploaded') continue;
            if (entry.serverUrl) {
              rec.viewerUrl = entry.serverUrl;
            }
            var serverPreview = entry.serverSmallUrl || entry.serverUrl;
            if (!serverPreview) continue;
            if (rec.previewUrl === serverPreview) continue;
            var oldUrl = rec.previewUrl;
            rec.previewUrl = serverPreview;
            // Only revoke if the previous value was a real Blob URL we created.
            if (typeof oldUrl === 'string' && oldUrl.indexOf('blob:') === 0) {
              try { URL.revokeObjectURL(oldUrl); } catch (e) { /* noop */ }
            }
          }
        },
      },
    },
    methods: {
      change(event) {
        var input = event && event.target ? event.target : null;
        var fileList = input && input.files ? input.files : null;
        if (this.managedSubmit) {
          if (fileList && fileList.length > 0) {
            var files = [];
            for (var i = 0; i < fileList.length; i++) {
              files.push(fileList[i]);
            }
            this.addFiles(files);
          }
          if (input) {
            input.value = '';
          }
        } else {
          if (fileList && fileList.length > 0) {
            this.legacyFiles = fileList;
          } else {
            this.legacyFiles = {};
          }
        }
        this.$emit('change', event);
      },
      addFiles(files) {
        var self = this;
        if (!this.managedSubmit) {
          // Legacy mode: keep prior synchronous behavior unchanged for BookingWidget.
          files.forEach(function (file) {
            var isImage = !!(file.type && file.type.indexOf('image/') === 0);
            var rec = {
              recordId: self.nextRecordId++,
              file: file,
              previewUrl: null,
              isImage: isImage,
              status: 'ready',
              error: null,
              formattedSize: self.formatBytes(file.size),
            };
            if (self.acceptImageOnly && !isImage) {
              rec.status = 'rejected';
              rec.error = self.rejectedNonImageMessage;
            } else if (isImage) {
              try {
                rec.previewUrl = URL.createObjectURL(file);
              } catch (e) {
                rec.previewUrl = null;
              }
            }
            self.records.push(rec);
          });
          return;
        }
        // Managed mode: strict async validation that preserves selection order
        // via Promise.all (resolves in original index order). Invalid files are
        // NEVER pushed to records; one batched SweetAlert summarises the batch.
        var validations = files.map(function (file) { return self.validateImageFile(file); });
        Promise.all(validations).then(function (results) {
          var invalidCount = 0;
          results.forEach(function (res) {
            if (res && res.ok) {
              var rec = {
                recordId: self.nextRecordId++,
                file: res.file,
                previewUrl: res.previewUrl,
                isImage: true,
                status: 'ready',
                error: null,
                formattedSize: self.formatBytes(res.file.size),
              };
              self.records.push(rec);
            } else {
              invalidCount++;
            }
          });
          if (invalidCount > 0) {
            self.showInvalidFileAlert(invalidCount);
          }
          // Managed-mode: notify parent only after async validation is
          // complete so consumers see the actual accepted records list.
          self.$emit('records-changed', self.getSelectedFileRecords());
        });
      },
      getFileExtension(file) {
        if (!file || !file.name) return '';
        var dot = file.name.lastIndexOf('.');
        if (dot < 0) return '';
        return file.name.substring(dot + 1).toLowerCase();
      },
      isSupportedImageFile(file) {
        if (!file) return false;
        var ext = this.getFileExtension(file);
        if (SUPPORTED_IMAGE_EXTENSIONS.indexOf(ext) < 0) return false;
        var mime = (file.type || '').toLowerCase();
        if (!mime) {
          // Some browsers/OSes leave file.type empty for certain files. Allow
          // through to decode validation if the extension is supported.
          return true;
        }
        return SUPPORTED_IMAGE_MIME_TYPES.indexOf(mime) >= 0;
      },
      validateImageFile(file) {
        var self = this;
        return new Promise(function (resolve) {
          if (!self.isSupportedImageFile(file)) {
            resolve({ ok: false, file: file });
            return;
          }
          var url;
          try {
            url = URL.createObjectURL(file);
          } catch (e) {
            resolve({ ok: false, file: file });
            return;
          }
          var img = new Image();
          img.onload = function () {
            // Reuse the validation object URL as the preview URL; it is
            // revoked later by removeRecord / revokeAllPreviewUrls.
            resolve({ ok: true, file: file, previewUrl: url });
          };
          img.onerror = function () {
            try { URL.revokeObjectURL(url); } catch (e) {}
            resolve({ ok: false, file: file });
          };
          img.src = url;
        });
      },
      buildInvalidImageMessage() {
        if (this.locales && this.locales.common && this.locales.common.upload_wrong) {
          return this.locales.common.upload_wrong + ' ' + SUPPORTED_FORMATS_LABEL;
        }
        return 'Unsupported or corrupt image file. Supported formats: ' + SUPPORTED_FORMATS_LABEL + '.';
      },
      invalidAlertTitle() {
        if (this.locales && this.locales.common && this.locales.common.error) {
          return this.locales.common.error;
        }
        return 'Error';
      },
      showInvalidFileAlert(/* count */) {
        var title = this.invalidAlertTitle();
        var message = this.buildInvalidImageMessage();
        // Mirror admin pattern (PropertyPhotoUploader.js): prefer window.app.sweetalert.
        if (typeof window !== 'undefined' && window.app && typeof window.app.sweetalert === 'function') {
          window.app.sweetalert(title, message, 'error');
          return;
        }
        // Silent no-op if sweetalert is not globally available; do not throw.
      },
      handleImageError(rec) {
        if (!rec) return;
        // Belt-and-suspenders: a record passed validation but the rendered
        // <img> still failed (race, mid-revoke). Remove silently-with-alert.
        this.removeRecord(rec.recordId);
        this.showInvalidFileAlert(1);
      },
      requestRemoveRecord(recordId) {
        var self = this;
        if (!this.confirmBeforeRemove || !this.removeConfirmText) {
          this.removeRecord(recordId);
          return;
        }
        if (this.pendingRemoveIds[recordId]) return;
        var swal = (typeof window !== 'undefined' && window.app && typeof window.app.sweetalert === 'function')
          ? window.app.sweetalert
          : null;
        if (!swal) {
          this.removeRecord(recordId);
          return;
        }
        this.pendingRemoveIds[recordId] = true;
        swal({
          text: this.removeConfirmText,
          icon: 'warning',
          buttons: [
            this.removeConfirmCancel || (this.locales && this.locales.common && this.locales.common.cancel) || 'Cancel',
            this.removeConfirmOk || this.removeLabel,
          ],
          dangerMode: true,
        }).then(function (value) {
          delete self.pendingRemoveIds[recordId];
          if (!value) return;
          self.removeRecord(recordId);
        });
      },
      removeRecord(recordId) {
        var idx = -1;
        for (var i = 0; i < this.records.length; i++) {
          if (this.records[i].recordId === recordId) {
            idx = i;
            break;
          }
        }
        if (idx < 0) return;
        var rec = this.records[idx];
        if (rec.previewUrl) {
          try { URL.revokeObjectURL(rec.previewUrl); } catch (e) {}
        }
        this.records.splice(idx, 1);
        this.$emit('change', { target: { files: this.getSelectedFiles() } });
        this.$emit('records-changed', this.getSelectedFileRecords());
      },
      getSelectedFiles() {
        return this.acceptedRecords.map(function (r) { return r.file; });
      },
      getSelectedFileRecords() {
        return this.acceptedRecords.map(function (r) {
          return {
            recordId: r.recordId,
            file: r.file,
            previewUrl: r.previewUrl,
            thumbnailUrl: r.previewUrl,
            // viewerUrl = full-size; previewUrl = thumbnail.
            viewerUrl: r.viewerUrl || r.previewUrl,
            isImage: r.isImage,
            status: r.status,
          };
        });
      },
      clearInput() {
        this.revokeAllPreviewUrls();
        this.records = [];
        this.legacyFiles = {};
        if (this.id && this.$refs[this.id]) {
          this.$refs[this.id].value = '';
        }
      },
      revokeAllPreviewUrls() {
        this.records.forEach(function (rec) {
          if (rec.previewUrl) {
            try { URL.revokeObjectURL(rec.previewUrl); } catch (e) {}
          }
        });
      },
      formatBytes(bytes) {
        if (bytes === null || bytes === undefined || isNaN(bytes)) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      },
      fileExtension(filename) {
        if (!filename) return '';
        var dot = filename.lastIndexOf('.');
        if (dot < 0) return '';
        return filename.substring(dot + 1).toUpperCase();
      },
      handleUploadingCompleted() {
        this.clearInput();
      },
      seedPreUploadedPhotos(photo) {
        if (!photo || typeof photo.id === 'undefined' || !photo.url) return null;
        var rec = {
          recordId: this.nextRecordId++,
          file: null,
          previewUrl: photo.url,
          isImage: true,
          status: 'ready',
          error: null,
          formattedSize: '',
        };
        this.records.push(rec);
        return rec.recordId;
      },
      statusBadge(recordId) {
        var entry = this.statusMap && this.statusMap[recordId];
        if (!entry || !entry.status) return null;
        return entry.status;
      },
      statusLabel(recordId) {
        var entry = this.statusMap && this.statusMap[recordId];
        if (!entry) return '';
        return entry.label || entry.status || '';
      },
      errorMessage(recordId) {
        var entry = this.statusMap && this.statusMap[recordId];
        if (!entry) return '';
        return entry.errorMessage || '';
      },
      onRetryClick(recordId) {
        this.$emit('retry-record', recordId);
      },
      triggerNativeInput() {
        if (this.id && this.$refs[this.id]) {
          this.$refs[this.id].click();
        }
      },
      onDrop(event) {
        if (!this.managedSubmit) return;
        if (!event || !event.dataTransfer || !event.dataTransfer.files) return;
        var dropped = Array.prototype.slice.call(event.dataTransfer.files);
        if (dropped.length === 0) return;
        this.addFiles(dropped);
      },
    },
    created() {
      this.$root.$on('uploading-completed', this.handleUploadingCompleted);
    },
    beforeDestroy() {
      this.revokeAllPreviewUrls();
      this.$root.$off('uploading-completed', this.handleUploadingCompleted);
    },
  };
</script>
